// AudiologyActivePatientCard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "../../../api/axiosConfig";
import "../../../center admin css/LabReportsEntry.css";
import "../../../center admin css/centerAdmin.css";

/** ---- Minimal API types ---- */
type QueueItem = {
  _id: string;
  createdAt?: string;
  startedAt?: string;
  stationId?: string;
  sessionId?: string;
  session_id?: string;
  session?: { id?: string; sessionId?: string };
  child?: {
    childId?: string;
    name?: string;
    age?: number;
    gender?: string;
    emrId?: string;
    profile?: string;
  };
};

type StationPatientsResp = {
  waiting?: QueueItem[];
  inProgress?: QueueItem[];
  completed?: QueueItem[];
};

type Queues = {
  waiting: QueueItem[];
  inProgress: QueueItem[];
  completed: QueueItem[];
};

interface ActivePatientCardProps {
  onPatientSelect?: (p: {
    id: string;
    name: string;
    age: number;
    gender: string;
    emrId?: string;
  }) => void;
  showActions?: boolean;
  sessionId?: string;
  stationId?: string;
  validateBeforeComplete?: () => boolean;
  onStationStarted?: () => void;
}

declare global {
  interface Window {
    audiologyFormCompleted?: boolean;
  }
}

function readSessionIdFromItem(it?: QueueItem): string | undefined {
  if (!it) return undefined;
  return (
    it.sessionId ||
    (it as any).session_id ||
    it.session?.id ||
    it.session?.sessionId ||
    undefined
  );
}

function readSessionIdFromStorage(): string | undefined {
  return (
    sessionStorage.getItem("workflowSessionId") ||
    sessionStorage.getItem("lastSessionId") ||
    sessionStorage.getItem("currentSessionId") ||
    undefined
  );
}

function clearStoredSessionIds() {
  ["workflowSessionId", "lastSessionId", "currentSessionId"].forEach((k) =>
    sessionStorage.removeItem(k),
  );
}

const AudiologyActivePatientCard: React.FC<ActivePatientCardProps> = ({
  onPatientSelect,
  showActions = true,
  sessionId,
  stationId = "AUDIOLOGY",
  validateBeforeComplete, // ✅ ADD THIS
  onStationStarted, // ✅ ADD THIS
}) => {
  const [loading, setLoading] = useState(false);
  // ✅ NEW: State for avatar
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();

  const [patients, setPatients] = useState<Queues>({
    waiting: [],
    inProgress: [],
    completed: [],
  });
  const [effectiveSessionId, setEffectiveSessionId] = useState<string>();
  // const { activePatient } = useOutletContext<any>();
  const outlet = useOutletContext<any>();

  const { activePatient, setActivePatient } = outlet;

  const loadQueues = async () => {
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found. Please log in.");
      const headers = { Authorization: `Bearer ${token}` };

      const centerId =
        sessionStorage.getItem("centerId") || localStorage.getItem("centerId");

      const effectiveDate = new Date().toISOString().slice(0, 10);

      const { data } = await api.centerworkflowApi.get(
        `/queues/AUDIOLOGY_Q?center_id=${encodeURIComponent(centerId || "")}&date=${encodeURIComponent(effectiveDate)}`,
        { headers },
      );

      setPatients({
        waiting: [],
        inProgress: data?.inProgress ?? [],
        completed: [],
      });
    } catch (e) {
      console.warn("[AudiologyCard] load queue failed:", e);
      setPatients({ waiting: [], inProgress: [], completed: [] });
    }
  };

  useEffect(() => {
    loadQueues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  // const current = activePatient;

  const current = useMemo(() => {
    if (activePatient) return activePatient;

    if (patients.inProgress.length > 0) {
      return patients.inProgress[0];
    }

    return null;
  }, [activePatient, patients.inProgress]);

  useEffect(() => {
    const fromItem = readSessionIdFromItem(current);
    const fromStore = readSessionIdFromStorage();
    const resolved = sessionId || fromItem || fromStore;
    setEffectiveSessionId(resolved);
  }, [sessionId, current]);

  /* ================= IMAGE FETCH LOGIC (FIXED) ================= */
  /* ================= IMAGE FETCH LOGIC ================= */
  useEffect(() => {
    const loadAvatar = async () => {
      setAvatarUrl(undefined);

      const profileKey = current?.child?.profile;

      if (!profileKey) return;

      try {
        const token = sessionStorage.getItem("accessToken");
        if (!token) return;

        const headers = {
          Authorization: `Bearer ${token}`,
        };

        // If backend already gives a presigned URL, use it directly.
        if (profileKey.includes("X-Amz-Signature")) {
          setAvatarUrl(profileKey);
          return;
        }

        const parts = profileKey.split("/");

        if (parts.length < 3) {
          console.warn("Invalid profile key:", profileKey);
          return;
        }

        // Example:
        // checkin/capture-photo/P-2026-0000113-0/filename.jpg
        //
        // module = checkin/capture-photo
        // key    = P-2026-0000113-0/filename.jpg

        const module = `${parts[0]}/${parts[1]}`;
        const key = parts.slice(2).join("/");

        const response = await api.S3config.get("/view-url", {
          params: {
            module,
            key,
          },
          headers,
        });

        const signedUrl = response.data?.url || (response as any).url;

        if (signedUrl) {
          setAvatarUrl(signedUrl);
        }
      } catch (err) {
        console.error("Audiology avatar load failed:", err);
      }
    };

    loadAvatar();
  }, [current?.child?.profile]);
  /* ================================================= */
  /* ================================================= */

  /* ================================================= */

  useEffect(() => {
    if (!current) return;
    const age = typeof current.child?.age === "number" ? current.child!.age : 0;

    const g = current.child?.gender
      ? current.child.gender.toString().toUpperCase()
      : "";

    onPatientSelect?.({
      id: current._id,
      name: current.child?.name || "-",
      age,
      gender: g ? (g.startsWith("F") ? "Female" : "Male") : "-",
      emrId: current.child?.childId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?._id]);

  async function doPost(path: "start" | "complete" | "cancel") {
    if (!effectiveSessionId) {
      alert(
        "No active session id found. Pick a patient or start a new session.",
      );
      return;
    }
    try {
      setLoading(true);
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found.");
      const headers = { Authorization: `Bearer ${token}` };

      let url = "";

      if (path === "cancel") {
        url = `/sessions/${encodeURIComponent(
          effectiveSessionId,
        )}/stations/AUDIOLOGY/cancel`;
      } else {
        url = `/sessions/${encodeURIComponent(
          effectiveSessionId,
        )}/stations/${encodeURIComponent(stationId)}/${path}`;
      }

      let body: any = {};

      if (path === "complete") {
        body = {
          completedForms: ["AUDIOLOGY_SCREENING"],
          payload: {},
        };
      }

      const res = await api.centerworkflowApi.post(url, body, { headers });

      if (path === "start") {
        alert(`Station "${stationId}" started successfully`);

        onStationStarted?.();

        window.dispatchEvent(new Event("patientUpdated"));

        return;
      } else if (path === "cancel") {
        alert(`Station "${stationId}" cancelled successfully`);
      } else {
        if (res?.data?.isComplete) {
          clearStoredSessionIds();
          setActivePatient?.(null);

          sessionStorage.removeItem("selectedPatient_AUDIOLOGY_Q");
          alert("🎉 Workflow completed for this session");
        } else if (res?.data?.nextStation) {
          clearStoredSessionIds();
          setActivePatient?.(null);

          sessionStorage.removeItem("selectedPatient_AUDIOLOGY_Q");
          alert(`Next station: ${res.data.nextStation}`);
        } else {
          clearStoredSessionIds();
          setActivePatient?.(null);

          sessionStorage.removeItem("selectedPatient_AUDIOLOGY_Q");
          alert("Station completed");
        }
        // ✅ Notify Operations Board to refresh
        window.dispatchEvent(new Event("patientUpdated"));
      }
      // await loadQueues();

      await loadQueues();
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ?? e?.message ?? "Failed to call station API";
      alert(msg);
    } finally {
      setLoading(false);
    }
  }

  const ageText =
    typeof current?.child?.age === "number"
      ? `${current?.child?.age} Years`
      : "-";

  const genderText = current?.child?.gender
    ? String(current.child.gender).toUpperCase().startsWith("F")
      ? "Female"
      : "Male"
    : "-";

  return (
    <div className="patient-details-card" style={{ marginBottom: 18 }}>
      <div className="patient-details-top">
        {/* ✅ NEW: Display Avatar */}
        <div className="patient-avatar-large" style={{ overflow: "hidden" }}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Patient"
              className="patient-avatar-img"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div className="patient-avatar-fallback" />
          )}
        </div>

        <div className="patient-info-group">
          <div className="patient-info-row">
            <div>
              <span className="field-label">EMR ID</span>
              <div className="field-value">
                {current?.child?.childId ?? "-"}
              </div>
            </div>
            <div>
              <span className="field-label">Name</span>
              <div className="field-value">{current?.child?.name ?? "-"}</div>
            </div>
          </div>
          <div className="patient-info-row">
            <div>
              <span className="field-label">Age</span>
              <div className="field-value">{ageText}</div>
            </div>
            <div>
              <span className="field-label">Gender</span>
              <div className="field-value">{genderText}</div>
            </div>
          </div>
        </div>
      </div>

      {showActions && (
        <div
          className="patient-action-buttons"
          style={{ marginTop: 16, display: "flex", gap: "12px" }}
        >
          <button
            className="start-btn"
            style={{
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => current && doPost("start")}
            disabled={loading || !current}
          >
            {loading ? "Starting..." : "Start"}
          </button>

          <button
            className="cancel-button"
            style={{
              backgroundColor: "#FF9800", // 🟠 ORANGE
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => doPost("cancel")}
            disabled={loading}
          >
            {loading ? "Cancelling..." : "Cancel"}
          </button>

          <button
            className="end-btn"
            style={{
              backgroundColor: "#f44336",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => {
              if (validateBeforeComplete && !validateBeforeComplete()) return;
              doPost("complete");
            }}
            disabled={loading}
          >
            {loading ? "Completing..." : "Complete"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AudiologyActivePatientCard;
